/**
 * The success envelope every settings call shares: `{ success, code, message, payload }`.
 * A body that is not that shape is a contract error. The screen prints `message`.
 */
import { z } from "zod";
import { AppError } from "@/api/problems";

const baseResponseSchema = z.object({
  success: z.boolean().optional(),
  code: z.string().optional(),
  message: z.string().optional(),
});

export function payloadOf<T>(schema: z.ZodType<T>, body: unknown, path: string, status: number): T {
  const payload =
    body && typeof body === "object" && "payload" in body
      ? (body as { payload?: unknown }).payload
      : undefined;
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new AppError("contract", {
      status,
      cause: parsed.error,
      message: `${path} returned a payload this build does not expect`,
    });
  }
  return parsed.data;
}

const pageCount = z.number().int().nonnegative().optional();

/**
 * A paged read. `totalElements` and `totalPages` sit beside `payload`.
 * Either count may be absent; an absent count is not turned into `0`.
 * A count that is not a whole number of zero or more is a contract error.
 */
export function pageOf<T>(
  schema: z.ZodType<T>,
  body: unknown,
  path: string,
  status: number,
): { payload: T; totalElements: number | undefined; totalPages: number | undefined } {
  const parsed = z.object({ totalElements: pageCount, totalPages: pageCount }).safeParse(body);
  if (!parsed.success) {
    throw new AppError("contract", {
      status,
      cause: parsed.error,
      message: `${path} returned a payload this build does not expect`,
    });
  }
  return {
    payload: payloadOf(schema, body, path, status),
    totalElements: parsed.data.totalElements,
    totalPages: parsed.data.totalPages,
  };
}

/** Update and delete answer `BaseResponse` and no record. */
export function acceptBaseResponse(body: unknown, path: string, status: number): void {
  const parsed = baseResponseSchema.safeParse(body);
  if (!parsed.success) {
    throw new AppError("contract", {
      status,
      cause: parsed.error,
      message: `${path} returned a payload this build does not expect`,
    });
  }
}
